import { Input, Select, Table } from 'antd';
import { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useLifecycles } from 'react-use';

import { PageHeader } from '@ant-design/pro-components';

import { refinanceStateReset } from '../../features/refinance/refinanceSlice';
import { numberWithCommas } from '../../helpers/number';
import { getRefinanceFinanceListingThunk } from '../../services/refinanceService/refinanceThunk';
import { AppDispatch, RootState } from '../../store/store';
import { displayDate } from '../../utils/datetime.util';

import type { Refinance } from '../../features/refinance/refinanceSlice';
import type { ColumnsType } from 'antd/es/table';
import type { TableProps } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { getAdminsThunk } from '../../services/adminService/adminThunk';
import AccountEnquiryModal from '../AccountEnquiry/AccountEnquiryModal';
import { calcRefinanceAdjustedOutstandingBalance } from '../../utils/calculation.util';

const { Search } = Input;

const CompletedRefinanceListingPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { refinanceFinanceList } = useSelector((state: RootState) => state.refinance);

  const [searchFiled, setSearchFiled] = useState('applicationNo');
  const [searchValue, setSearchValue] = useState('');
  const initialParams = useMemo(
    () => ({
      pagination: {
        current: 1,
        pageSize: 20,
      },
      sortOrder: 'desc',
      sortField: 'submissionDate',
      search: {},
      filters: {},
    }),
    []
  );

  const [sortedInfo, setSortedInfo] = useState<SorterResult<Refinance>>({});
  const [filterState, setFilterState] = useState(initialParams.filters);
  const [paginationState, setPaginationState] = useState(initialParams.pagination);

  const tablePagination = {
    total: refinanceFinanceList?.data?.total,
    showSizeChanger: true,
    showTotal: (total: number, range: number[]) => `${range[0]}-${range[1]} of ${total} items`,
    current: paginationState.current,
    pageSize: paginationState.pageSize,
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Agreement Date',
      dataIndex: ['application', 'agreement', 'signDate'],
      key: 'signDate',
      sorter: (a, b) => a.application.agreement.signDate - b.application.agreement.signDate,
      sortOrder: sortedInfo.columnKey === 'signDate' ? sortedInfo.order : null,
      render: (text) => (text ? displayDate(text) : '-'),
    },
    {
      title: 'New Agreement No.',
      dataIndex: ['application', 'agreement', 'agreementNo'],
      key: 'agreementNo',
      render: (text, record) => <AccountEnquiryModal key={text} agreementNo={text} />,
    },
    {
      title: 'Old Agreement No.',
      dataIndex: ['oldAgreementNo'],
      key: 'oldAgreementNo',
      render: (text, record) => <AccountEnquiryModal key={text} agreementNo={text} />,
    },
    {
      title: 'Applicant Name',
      dataIndex: 'user',
      key: 'user',
      render: (text, record) => record?.application?.applicantFirstName ?? '-',
    },
    {
      title: 'Applicant NRIC',
      dataIndex: 'user',
      key: 'user',
      render: (text, record) => record?.application?.applicantNric ?? '-',
    },
    {
      title: 'Total Outstanding Balance',
      dataIndex: 'totalLoanOutstanding',
      key: 'totalLoanOutstanding',
      render: (text, record) => `RM ${numberWithCommas(text ?? 0)}`,
    },
    {
      title: 'Refinancing Fees',
      dataIndex: 'refinanceFee',
      key: 'refinanceFee',
      render: (text, record) => `RM ${numberWithCommas(text ?? 0)}`,
    },
    {
      title: 'Pre-Execution Payment',
      dataIndex: [],
      key: 'preExecutionPayment',
      render: (text, record) =>
        `RM ${numberWithCommas(
          calcRefinanceAdjustedOutstandingBalance(
            Number(record.totalLoanOutstanding),
            Number(record.refinanceFee),
            Number(record.application.financeAmount)
          ) ?? 0
        )}`,
    },
    {
      title: 'Refinance Amount',
      dataIndex: ['application', 'financeAmount'],
      key: 'financeAmount',
      render: (text, record) => `RM ${numberWithCommas(text ?? 0)}`,
    },
  ];

  const fetchRefinances = async (params?: any) => {
    await dispatch(getRefinanceFinanceListingThunk(params));
  };

  const fetchAdmins = async (params?: any) => {
    await dispatch(getAdminsThunk({ params }));
  };

  const resetRefinanceState = async () => {
    await dispatch(refinanceStateReset());
  };

  const onSearch = (value: string) => {
    value = value.trim();
    setSearchValue(value);
    if (value) {
      fetchRefinances({
        ...initialParams,
        filters: { ...filterState },
        search: {
          [searchFiled]: value,
        },
      });
    } else {
      fetchRefinances({
        ...initialParams,
        filters: { ...filterState },
      });
    }

    setFilterState({ ...initialParams.filters });
  };

  const handleTableChange: TableProps<Refinance>['onChange'] = async (
    pagination,
    filters,
    sorter: any
  ) => {
    const param =
      searchValue.length === 0
        ? {
            ...initialParams,
            ...filterState,
            pagination,
            sortField: sorter?.columnKey ?? initialParams.sortField,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
          }
        : {
            ...initialParams,
            ...filterState,
            pagination,
            sortField: sorter?.columnKey ?? initialParams.sortField,
            sortOrder: sorter?.order === 'ascend' ? 'asc' : 'desc',
            search: {
              [searchFiled]: searchValue,
            },
          };

    fetchRefinances(param);
    setSortedInfo(sorter as SorterResult<Refinance>);
    setPaginationState({
      current: pagination.current ?? 1,
      pageSize: pagination.pageSize ?? 10,
    });
  };

  useLifecycles(
    () => {
      resetRefinanceState();
      fetchRefinances(initialParams);
      fetchAdmins({
        pagination: {
          current: 1,
          pageSize: 10000, // get all admins
        },
      });
    },
    () => {
      resetRefinanceState();
    }
  );

  return (
    <>
      <Helmet>
        <title>Completed Refinance Listing - redCASH CEP</title>
      </Helmet>
      <PageHeader className="px-0 mb-2" title="Completed Refinance Listing" />
      {/* <Radio.Group defaultValue="a" buttonStyle="solid" optionType="button">
        <Radio.Button value="a">All</Radio.Button>
        <Radio.Button value="b">Credit Assessment (Approval)</Radio.Button>
        <Radio.Button value="c">Credit Assessment (KIV Approval)</Radio.Button>
        <Radio.Button value="d">System Rejected</Radio.Button>
      </Radio.Group> */}
      <Input.Group compact>
        <Select
          defaultValue="applicationNo"
          style={{ width: 200 }}
          onChange={(value) => setSearchFiled(value)}
          options={[
            { value: 'applicationNo', label: 'Application No.' },
            { value: 'applicantFirstName', label: 'Applicant Name' },
            { value: 'applicantNric', label: 'Applicant NRIC' },
          ]}
        />
        <Search placeholder="input search text" onSearch={onSearch} style={{ width: 200 }} />
      </Input.Group>
      <br />
      <Table
        rowKey="id"
        className="min-w-fit"
        columns={columns}
        pagination={tablePagination}
        dataSource={refinanceFinanceList?.data?.data ?? []}
        loading={refinanceFinanceList?.isLoading}
        onChange={handleTableChange}
      />
    </>
  );
};

export default CompletedRefinanceListingPage;
